import { authFetch, clearToken } from "../utils/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Target, BookOpen, Plus, Pencil, Trash2, Users,
  BarChart3, Settings, X, Save, UserPlus, Search, Check
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = window.location.origin;

const CATEGORIES = ["putting", "short_game_0_20", "short_game_20_40", "wedge_play_40_100", "irons", "driver"];
const CAT_COLORS = {
  putting: "bg-emerald-500/20 text-emerald-700 border-emerald-500/30",
  short_game_0_20: "bg-blue-500/20 text-blue-700 border-blue-500/30",
  short_game_20_40: "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
  wedge_play_40_100: "bg-rose-500/20 text-rose-700 border-rose-500/30",
  irons: "bg-amber-500/20 text-[#111] border-amber-500/30",
  driver: "bg-red-500/20 text-red-700 border-red-500/30",
};
const CAT_LABELS = {
  putting: "Putting",
  short_game_0_20: "Short Game (0-20)",
  short_game_20_40: "Short Game (20-40)",
  wedge_play_40_100: "Wedge Play (40-100)",
  irons: "Irons",
  driver: "Driver",
};
const PHASES_BY_CATEGORY = {
  putting: ["line", "pace", "pressure"],
  short_game_0_20: ["landing", "control", "up_down"],
  short_game_20_40: ["distance", "control", "scoring"],
  wedge_play_40_100: ["numbers", "control", "scoring"],
  irons: ["strike", "direction", "distance"],
  driver: ["start_line", "shape", "fairway"],
};

export default function CoachLibrary() {
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignPriority, setAssignPriority] = useState("preferred");
  const [form, setForm] = useState({ name: "", category: "putting", phase: "", description: "", instructions: "", finish_goal: "", duration_minutes: 10 });

  useEffect(() => {
    fetchDrills();
    fetchStudents();
  }, []);

  const fetchDrills = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/library`);
      if (res.ok) setDrills(await res.json());
      else if (res.status === 403) navigate("/dashboard");
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStudents = async () => {
    try {
      const res = await authFetch(`${API}/api/coach/students`);
      if (res.ok) {
        const data = await res.json();
        setStudents(data.students || []);
      }
    } catch (e) { console.error(e); }
  };

  const saveDrill = async () => {
    if (!form.name || !form.description) { toast.error("Name and description are required"); return; }
    try {
      const url = editingDrill ? `${API}/api/coach/library/${editingDrill}` : `${API}/api/coach/library`;
      const res = await authFetch(url, {
        method: editingDrill ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(editingDrill ? "Drill updated" : "Drill created");
        resetForm();
        fetchDrills();
      }
    } catch (e) { toast.error("Failed to save drill"); }
  };

  const deleteDrill = async (drillId) => {
    try {
      const res = await authFetch(`${API}/api/coach/library/${drillId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Drill deleted"); fetchDrills(); }
    } catch (e) { toast.error("Failed to delete drill"); }
  };

  const assignDrill = async (drillId) => {
    if (selectedStudents.length === 0) { toast.error("Select at least one student"); return; }
    try {
      const res = await authFetch(`${API}/api/coach/library/${drillId}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ drill_id: drillId, student_ids: selectedStudents, priority: assignPriority })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Drill assigned");
        setShowAssign(null);
        setSelectedStudents([]);
      }
    } catch (e) { toast.error("Failed to assign drill"); }
  };

  const editDrill = (drill) => {
    setEditingDrill(drill.drill_id);
    setForm({
      name: drill.name,
      category: drill.category,
      phase: drill.phase || "",
      description: drill.description,
      instructions: drill.instructions || "",
      finish_goal: drill.finish_goal || "",
      duration_minutes: drill.duration_minutes
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingDrill(null);
    setForm({ name: "", category: "putting", phase: "", description: "", instructions: "", finish_goal: "", duration_minutes: 10 });
  };

  const getCatLabel = (cat) => CAT_LABELS[cat] || cat.replace(/_/g, " ");


  const toggleStudent = (id) => {
    setSelectedStudents(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const filtered = drills.filter(d =>
    (filterCat === "all" || d.category === filterCat) &&
    (d.name?.toLowerCase().includes(search.toLowerCase()) || d.description?.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#111]" />
            </div>
            <div>
              <span className="font-bold text-lg text-[#111]">GolfFlowPro</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 border-purple-500/30">Coach</Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => navigate("/coach/settings")} className="text-[#555] hover:text-[#111]">
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Nav */}
      <nav className="px-6 py-3 border-b border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto flex gap-1">
          {[
            { id: "dashboard", label: "Dashboard", icon: <Target className="w-4 h-4" />, path: "/coach" },
            { id: "students", label: "Students", icon: <Users className="w-4 h-4" />, path: "/coach/students" },
            { id: "library", label: "Drill Library", icon: <BookOpen className="w-4 h-4" />, path: "/coach/library" },
            { id: "analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, path: "/coach/analytics" }
          ].map((tab) => (
            <Button key={tab.id} variant="ghost" onClick={() => navigate(tab.path)}
              className={`flex items-center gap-2 ${tab.id === "library" ? "bg-gray-200 text-[#111]" : "text-[#555] hover:text-[#111]"}`}>
              {tab.icon} {tab.label}
            </Button>
          ))}
        </div>
      </nav>

      <main className="px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Title */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-[#111]">Drill Library</h1>
              <p className="text-[#555] text-sm">{drills.length} drill{drills.length !== 1 ? "s" : ""} in your library</p>
            </div>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="create-drill-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Drill
            </Button>
          </div>

          {/* Create/Edit Form */}
          <AnimatePresence>
            {showForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <Card className="bg-white border-gray-200">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-[#111] text-base">{editingDrill ? "Edit Drill" : "New Drill"}</CardTitle>
                    <Button variant="ghost" size="icon" onClick={resetForm} className="text-[#555] hover:text-[#111]">
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#555] text-sm">Name</Label>
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                          className="bg-gray-100 border-gray-200 text-[#111] mt-1" placeholder="e.g. Gate Putting Drill"
                          data-testid="drill-name-input" />
                      </div>
                      <div>
                        <Label className="text-[#555] text-sm">Category</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {CATEGORIES.map(cat => (
                            <button key={cat} onClick={() => setForm({ ...form, category: cat, phase: "" })}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                                form.category === cat ? CAT_COLORS[cat] + " ring-1 ring-white/20" : "bg-gray-100 text-[#555] hover:bg-gray-200"
                              }`} data-testid={`drill-cat-${cat}`}>{getCatLabel(cat)}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#555] text-sm">Phase <span className="text-[#555]/50 text-xs">(when in the session should this drill appear?)</span></Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {(PHASES_BY_CATEGORY[form.category] || []).map(ph => (
                          <button key={ph} onClick={() => setForm({ ...form, phase: ph })}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                              form.phase === ph ? "bg-blue-500/20 text-blue-700 border border-blue-500/30" : "bg-gray-100 text-[#555] hover:bg-gray-200 border border-transparent"
                            }`} data-testid={`drill-phase-${ph}`}>{ph.replace("_", " ")}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#555] text-sm">Description</Label>
                      <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] mt-1 min-h-[80px]" placeholder="Brief description of the drill"
                        data-testid="drill-description-input" />
                    </div>
                    <div>
                      <Label className="text-[#555] text-sm">Instructions</Label>
                      <Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] mt-1 min-h-[80px]" placeholder="Step-by-step instructions for the player" />
                    </div>
                    <div>
                      <Label className="text-[#555] text-sm">Finish Goal <span className="text-[#555]/50 text-xs">(what does success look like?)</span></Label>
                      <Input value={form.finish_goal} onChange={e => setForm({ ...form, finish_goal: e.target.value })}
                        className="bg-gray-100 border-gray-200 text-[#111] mt-1" placeholder="e.g. 8/10 putts through the gate"
                        data-testid="drill-finish-goal-input" />
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-32">
                        <Label className="text-[#555] text-sm">Duration (min)</Label>
                        <Input type="number" value={form.duration_minutes}
                          onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 10 })}
                          className="bg-gray-100 border-gray-200 text-[#111] mt-1" min={1} max={120}
                          data-testid="drill-duration-input" />
                      </div>
                      <div className="flex-1" />
                      <Button onClick={saveDrill} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-drill-btn">
                        <Save className="w-4 h-4 mr-2" /> {editingDrill ? "Update" : "Create"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
              <Input placeholder="Search drills..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 bg-white border-gray-200 text-[#111] placeholder:text-[#555]" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setFilterCat("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterCat === "all" ? "bg-white/15 text-[#111]" : "bg-gray-100 text-[#555] hover:bg-gray-200"}`}>
                All
              </button>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setFilterCat(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                    filterCat === cat ? CAT_COLORS[cat] : "bg-gray-100 text-[#555] hover:bg-gray-200"
                  }`}>{getCatLabel(cat)}</button>
              ))}
            </div>
          </div>

          {/* Drill List */}
          {filtered.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-12 text-center">
                <BookOpen className="w-12 h-12 text-[#555] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#111] mb-2">
                  {drills.length === 0 ? "Your Library is Empty" : "No drills match your filters"}
                </h3>
                <p className="text-[#555] mb-4">
                  {drills.length === 0 ? "Create your first custom drill to start building your library." : "Try a different search or category filter."}
                </p>
                {drills.length === 0 && (
                  <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create Your First Drill
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((drill, i) => (
                <motion.div key={drill.drill_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card className="bg-white border-gray-200 hover:border-white/20 transition-colors h-full" data-testid={`drill-card-${drill.drill_id}`}>
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex gap-1.5 flex-wrap">
                          <Badge className={CAT_COLORS[drill.category] || "bg-gray-500/20 text-[#555]"} variant="outline">
                            {getCatLabel(drill.category)}
                          </Badge>
                          {drill.phase && (
                            <Badge className="bg-blue-500/15 text-blue-700 border-blue-500/25" variant="outline">
                              {drill.phase.replace("_", " ")}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-[#555]">{drill.duration_minutes} min</span>
                      </div>
                      <h3 className="text-[#111] font-semibold mb-1">{drill.name}</h3>
                      <p className="text-[#555] text-sm flex-1 line-clamp-2 mb-1">{drill.description}</p>
                      {drill.finish_goal && (
                        <p className="text-emerald-700 text-xs font-medium mb-3">Goal: {drill.finish_goal}</p>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <span className="text-xs text-[#555]">Assigned {drill.times_assigned || 0}x</span>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="text-[#555] hover:text-emerald-700 h-8 w-8"
                            onClick={() => { setShowAssign(drill.drill_id); setSelectedStudents([]); setAssignPriority("preferred"); }}
                            data-testid={`assign-drill-${drill.drill_id}`}>
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-[#555] hover:text-blue-400 h-8 w-8"
                            onClick={() => editDrill(drill)} data-testid={`edit-drill-${drill.drill_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-[#555] hover:text-red-400 h-8 w-8"
                            onClick={() => deleteDrill(drill.drill_id)} data-testid={`delete-drill-${drill.drill_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Assign Modal */}
          <AnimatePresence>
            {showAssign && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
                onClick={() => setShowAssign(null)}>
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()} data-testid="assign-modal">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#111]">Assign to Students</h3>
                    <Button variant="ghost" size="icon" onClick={() => setShowAssign(null)} className="text-[#555]">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="mb-4">
                    <Label className="text-[#555] text-sm mb-2 block">Priority</Label>
                    <div className="flex gap-2">
                      {["preferred", "required"].map(p => (
                        <button key={p} onClick={() => setAssignPriority(p)}
                          className={`px-4 py-2 rounded-lg text-sm capitalize transition-all ${
                            assignPriority === p
                              ? p === "required" ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/30" : "bg-emerald-500/20 text-emerald-700 ring-1 ring-emerald-500/30"
                              : "bg-gray-100 text-[#555] hover:bg-gray-200"
                          }`}>{p}</button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <Label className="text-[#555] text-sm">Select Students</Label>
                    {students.length === 0 ? (
                      <p className="text-[#555] text-sm py-4 text-center">No students yet. Invite students first.</p>
                    ) : (
                      students.map(s => (
                        <button key={s.student_id} onClick={() => toggleStudent(s.student_id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                            selectedStudents.includes(s.student_id) ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-gray-100 border border-transparent hover:bg-gray-200"
                          }`} data-testid={`assign-student-${s.student_id}`}>
                          <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                            selectedStudents.includes(s.student_id) ? "bg-emerald-500 border-emerald-500" : "border-gray-500"
                          }`}>
                            {selectedStudents.includes(s.student_id) && <Check className="w-3 h-3 text-[#111]" />}
                          </div>
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-700 font-semibold text-sm">
                            {s.name?.charAt(0) || "?"}
                          </div>
                          <span className="text-[#111] text-sm">{s.name}</span>
                        </button>
                      ))
                    )}
                  </div>

                  <Button onClick={() => assignDrill(showAssign)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                    disabled={selectedStudents.length === 0} data-testid="confirm-assign-btn">
                    Assign to {selectedStudents.length} Student{selectedStudents.length !== 1 ? "s" : ""}
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
